
import { useState, ReactNode } from 'react'
import RequestForm from './components/RequestForm'
import RequestList from './components/RequestList'
import ApprovalForm from './components/ApprovalForm'

import { AuthProvider, useAuth } from './lib/auth/auth-context'
import { ProtectedRoute } from './lib/auth/ProtectedRoute'

import { CurrentUserProvider } from './lib/current-user'
import { FrontendPlugin } from './plugin-types-frontend'

import { setCurrentView as _otelSetView } from './lib/debug/otel-web'

import './App.css'

const pluginModules = import.meta.glob('./plugins/*.tsx', { eager: true });
const plugins: FrontendPlugin[] = Object.values(pluginModules)
  .map((m: any) => m.default)
  .filter((p): p is FrontendPlugin => p?.name);

function PluginProviders({ children }: { children: ReactNode }) {
  return plugins.reduce<ReactNode>(
    (acc, plugin) => {
      if (!plugin.Provider) return acc;
      const Provider = plugin.Provider;
      return <Provider>{acc}</Provider>;
    },
    children
  ) as JSX.Element;
}

type View = 'my_requests' | 'pending_approval' | 'select_template' | 'form' | 'edit' | 'approval' | string
const REQUEST_TEMPLATES = [{"description": "\u51fa\u5f35\u306b\u4f34\u3046\u8cbb\u7528\u306e\u4e8b\u524d\u627f\u8a8d\u7533\u8acb", "displayName": "\u51fa\u5f35\u7533\u8acb", "icon": "\u2708\ufe0f", "name": "business_trip"}];


function AppContent() {
  const { user, logout } = useAuth()

  const [currentView, setCurrentView] = useState<View>('my_requests')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)


  const handleViewChange = (view: View, id?: string, label?: string) => {
    setCurrentView(view)
    setSelectedId(id || null)

    _otelSetView(view, label)

  }


  const currentUser = { roles: user?.roles || [], displayName: user?.displayName || '', id: user?.id || '' }


  return (
    <CurrentUserProvider value={currentUser}>
      <div className="app-layout">
        <aside className="app-sidebar">
          <div className="sidebar-header">
            <h1>{document.title}</h1>
          </div>
          <nav className="sidebar-nav">
            {["requester"].some(r => currentUser.roles.includes(r)) && (
              <button data-testid="nav-new-request" className={`nav-item${currentView === 'select_template' || currentView === 'form' ? ' active' : ''}`} onClick={() => handleViewChange('select_template', undefined, '新規申請')}>新規申請</button>
            )}
            <button data-testid="nav-my-requests" className={`nav-item${currentView === 'my_requests' ? ' active' : ''}`} onClick={() => handleViewChange('my_requests', undefined, '申請一覧')}>申請一覧</button>
            {["supervisor"].some(r => currentUser.roles.includes(r)) && (
              <button data-testid="nav-pending-approval" className={`nav-item${currentView === 'pending_approval' ? ' active' : ''}`} onClick={() => handleViewChange('pending_approval', undefined, '承認一覧')}>承認一覧</button>
            )}
            {plugins.filter(p => p.Page && p.navLabel).map(p => (
              <button key={p.name} data-testid={`nav-${p.name}`} className={`nav-item${currentView === p.name ? ' active' : ''}`} onClick={() => handleViewChange(p.name, undefined, p.navLabel)}>{p.navLabel}</button>
            ))}
          </nav>

          <div className="sidebar-footer">
            <span className="user-info">{user?.displayName}（{user?.roles?.join(', ') || ''}）</span>
            <button onClick={logout} className="logout-btn">ログアウト</button>
          </div>

          {plugins.filter(p => p.HeaderItem).map(p => {
            const Item = p.HeaderItem!;
            return <Item key={p.name} />;
          })}
        </aside>

        <main className="app-main">
          {(currentView === 'my_requests' || currentView === 'pending_approval') && (
            <RequestList
              viewMode={currentView}
              onViewRequest={(id) => handleViewChange('approval', id)}
              onEditRequest={(id) => handleViewChange('edit', id)}
            />
          )}
          {currentView === 'select_template' && (
            <div className="template-select">
              <h2>申請テンプレートを選択</h2>
              <div className="template-grid">
                {REQUEST_TEMPLATES.map((tmpl: any) => (
                  <button
                    key={tmpl.name}
                    data-testid={`template-select-${tmpl.name}`}
                    className="template-card"
                    onClick={() => { setSelectedTemplate(tmpl.name); handleViewChange('form', undefined, '申請入力'); }}
                  >
                    <span className="template-icon">{tmpl.icon || '📝'}</span>
                    <span className="template-name">{tmpl.displayName}</span>
                    {tmpl.description && <span className="template-desc">{tmpl.description}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
          {currentView === 'form' && (
            <RequestForm templateName={selectedTemplate} onSuccess={() => handleViewChange('my_requests')} />
          )}
          {currentView === 'edit' && selectedId && (
            <RequestForm
              editId={selectedId}
              onSuccess={() => handleViewChange('my_requests')}
              onCancel={() => handleViewChange('my_requests')}
            />
          )}
          {currentView === 'approval' && selectedId && (
            <ApprovalForm requestId={selectedId} onBack={() => handleViewChange('my_requests')} />
          )}
          {plugins.filter(p => p.Page).map(p => {
            const PageComponent = p.Page!;
            return currentView === p.name ? <PageComponent key={p.name} /> : null;
          })}
        </main>
      </div>
    </CurrentUserProvider>
  )
}


function App() {
  return (
    <AuthProvider>
      <ProtectedRoute>
        <PluginProviders>
          <AppContent />
        </PluginProviders>
      </ProtectedRoute>
    </AuthProvider>
  )
}


export default App